import { fetchIsPostalCodeValid, isFacilitySubscribeProtectPlus } from '@/entities/facility/@x/rma';
import { INVALID_EMAIL_ERROR_MESSAGE } from '@/shared/config';
import { isValidPhone, GETTEXT_ALL_FACILITIES, validateEmail } from '@/shared/lib';
import _ from 'lodash';
import { t } from 'ttag';
import { RMA_STATUSES } from './rmaConstants';

/**
 * Maps a backend RMA ticket status onto the local RMA_STATUSES shown in the UI. New becomes PENDING, Rejected becomes REJECTED, and any other status is passed through unchanged.
 *
 * @param status - Backend RMA status string.
 * @returns The matching local RMA status.
 */
const convertRmaStatusToLocalStatus = (status: any) => {
  if (status === 'New') return RMA_STATUSES.PENDING;
  if (status === 'Rejected') return RMA_STATUSES.REJECTED;
  return status;
};

/**
 * Validates an RMA shipping or billing contact block, collecting one message per invalid field keyed by the type prefix. Phone and email formats are checked, country, street1, city and state are required, and US or CA zips are verified remotely through fetchIsPostalCodeValid after resolving the state against divisions, with a thrown error logged and reported as an invalid zipcode.
 *
 * @param contacts - Contact fields entered for the shipment.
 * @param divisions - State or province options used to resolve the postal division.
 * @param type - Field-name prefix identifying the contact block, defaulting to shipping.
 * @returns An object of field-keyed error messages, empty when the contact is valid.
 */
const handleValidateContact = async (contacts: any, divisions: any, type = 'shipping') => {
  const errorObject: any = {};
  if (contacts?.phone) {
    if (!isValidPhone(contacts?.phone, contacts?.country)) {
      _.assign(errorObject, { [`${type}Phone`]: t`Invalid value` });
    }
  } else {
    _.assign(errorObject, { [`${type}Phone`]: t`Contact phone number is required.` });
  }

  // *: Validate emails
  if (contacts.email) {
    if (!validateEmail(contacts.email)) {
      _.assign(errorObject, {
        [`${type}Email`]: t`The field contains email with invalid format, please check again!`,
      });
    }
  } else {
    _.assign(errorObject, { [`${type}Email`]: t`Contact email is required.` });
  }

  if (!contacts.country) {
    _.assign(errorObject, { [`${type}Country`]: t`Country is required.` });
  }

  if (!contacts.street1) {
    _.assign(errorObject, { [`${type}Address`]: t`Address is required.` });
  }

  if (!contacts.city) {
    _.assign(errorObject, { [`${type}City`]: t`City is required.` });
  }

  if (!contacts.state) {
    _.assign(errorObject, { [`${type}State`]: t`State is required.` });
  }

  // *: Validate shipping zipcode
  try {
    if (contacts.country === 'US' || contacts.country === 'CA') {
      const nzipCode =
        contacts.country === 'CA' && contacts.zip ? contacts.zip.split(' ')[0] : contacts.zip;
      if (nzipCode) {
        /**
         * The division entry whose code or label corresponds to the contact's state, taken from the supplied divisions list. Matched either by an exact value equality on the state code or by the state string being contained in the division label.
         *
         * @returns The matching division object, or undefined when none matches.
         */
        const division = _.find(
          divisions,
          (x) => x.value === contacts.state || _.includes(x.label, contacts.state),
        );
        const isPostalCodeValid = await fetchIsPostalCodeValid({
          division: division.fullCode,
          postalCode: nzipCode,
        });
        if (!isPostalCodeValid) {
          _.assign(errorObject, { [`${type}Zip`]: t`Invalid zipcode` });
        }
      } else {
        _.assign(errorObject, { [`${type}Zip`]: t`Zip code is required.` });
      }
    } else if (!contacts.zip) {
      _.assign(errorObject, { [`${type}Zip`]: t`Zip code is required.` });
    }
  } catch (error) {
    console.error('Failed to check zip code: ', error);
    _.assign(errorObject, { [`${type}Zip`]: t`Invalid zipcode` });
  }
  return errorObject;
};

/**
 * Validates the notification email entry of the RMA form before submission. Reports a required message when both the accumulated list and the pending input are empty, and the shared invalid-email message when the pending input fails validateEmail or the list is empty.
 *
 * @param emailList - Emails already accepted into the notification list.
 * @param emailInputValue - Email text still pending in the input.
 * @returns An object holding an invalidEmails message, empty when the entry is valid.
 */
const handleValidateNotificationEmail = (emailList: any, emailInputValue: any) => {
  const errorObject = {};

  if (!emailInputValue && _.isEmpty(emailList)) {
    _.assign(errorObject, { invalidEmails: t`Notification email is required.` });
  } else if ((emailInputValue && !validateEmail(emailInputValue)) || _.isEmpty(emailList)) {
    _.assign(errorObject, { invalidEmails: INVALID_EMAIL_ERROR_MESSAGE });
  }

  return errorObject;
};

/**
 * Determines whether Protect Plus coverage applies to the facility selection driving the RMA form. For the all-facilities sentinel it reports whether any supported facility's programs include Protect Plus, otherwise it inspects only the selected facility's programs.
 *
 * @param selectedFacility - Facility option currently selected in the form.
 * @param supportFacilities - Facilities the user supports, scanned for the all-facilities case.
 * @returns True when a relevant facility subscribes to Protect Plus.
 */
const checkHaveFacilitySubscribeProtectPlus = (selectedFacility: any, supportFacilities: any) => {
  if (selectedFacility.value === GETTEXT_ALL_FACILITIES) {
    return _.some(supportFacilities, (supportFacility) =>
      isFacilitySubscribeProtectPlus(supportFacility.programs),
    );
  }
  return isFacilitySubscribeProtectPlus(selectedFacility?.programs);
};

export {
  checkHaveFacilitySubscribeProtectPlus,
  convertRmaStatusToLocalStatus,
  handleValidateContact,
  handleValidateNotificationEmail,
};
